import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { Control, useFieldArray, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { ErrorInfo, TreeCompany } from '../../../../api/model';
import { usePostOrganizationsCheck } from '../../../../api/origanization/origanization';
import { useDefaultOptions } from '../../../../hooks/api';
import OkModal from '../../../modules/OkModal';
import { CompanyModal } from './CompanyModal';
import { Group } from './Group';
import { GroupModal } from './GroupModal';

type Props = {
  control: Control<{ companies: TreeCompany[] }>;
  getValues: UseFormGetValues<{ companies: TreeCompany[] }>;
  setValue: UseFormSetValue<{ companies: TreeCompany[] }>;
  companyIndex: number;
  company: TreeCompany;
};
function Company({ control, getValues, companyIndex, company, setValue }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);
  const [isCompanyModal, setIsCompanyModal] = useState(false);
  const [isGroupModal, setIsGroupModal] = useState(false);
  const [display, setDisplay] = useState(false);
  const { fields, append } = useFieldArray({ name: `companies.${companyIndex}.groups`, control });
  const [isCheckError, setIsCheckError] = useState(false);
  const { mutateAsync, error } = usePostOrganizationsCheck<AxiosError<ErrorInfo>, unknown>(options);
  const companyValue = getValues().companies[companyIndex];

  const handleHidden = async () => {
    await mutateAsync(
      {
        data: { organization_divisions: 1, hidden_id: companyValue?.company_id },
      },
      {
        onSuccess: () => {
          setValue(`companies.${companyIndex}.hidden`, companyValue?.hidden === 0 ? 1 : 0);
        },
        onError: () => {
          setIsCheckError(true);
        },
      }
    );
  };

  const ref = useRef(null);
  useClickAway(ref, () => {
    setDisplay(false);
  });

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setDisplay(true);
  };

  return (
    <div className="sys-group" key={`company-${String(company.company_id)}`}>
      <div className="sys-group1" ref={ref}>
        <p
          className={`name ${companyValue.hidden === 1 ? 'grayoff' : ''}`}
          style={{ cursor: 'pointer' }}
          onContextMenu={(e) => handleContextMenu(e)}
        >
          {companyValue.company_name_ja}
        </p>
        <ul className="pop" style={{ display: display ? 'block' : 'none' }}>
          <li className="modal-open" onClick={() => setIsGroupModal(true)}>
            <i className="fa-solid fa-circle-plus" />
            {t('グループ追加')}
          </li>

          <li onClick={() => handleHidden()}>
            <i className="fa-solid fa-eye-slash" />
            {companyValue?.hidden === 0 ? t('会社非表示') : t('会社表示')}
          </li>
          <li className="modal-open" onClick={() => setIsCompanyModal(true)}>
            <i className="fa-solid fa-circle-plus" />
            {t('会社編集')}
          </li>
        </ul>
      </div>
      <div className="sys-group2">
        {fields.map((group, i) => (
          <Group
            key={`group-${String(group.group_id)}`}
            groupSize={company.groups?.length || 0}
            companyIndex={companyIndex}
            groupIndex={i}
            getValues={getValues}
            setValue={setValue}
            control={control}
          />
        ))}
      </div>
      <CompanyModal
        companyIndex={companyIndex}
        open={isCompanyModal}
        onClose={() => setIsCompanyModal(false)}
        company={company}
        getValues={getValues}
        setValue={setValue}
      />
      <GroupModal
        companyIndex={companyIndex}
        open={isGroupModal}
        onClose={() => setIsGroupModal(false)}
        getValues={getValues}
        setValue={setValue}
        append={append}
      />
      {isCheckError && <OkModal onClickOk={() => setIsCheckError(false)}>{error?.response?.data?.message}</OkModal>}
    </div>
  );
}

export { Company };
